import { createContext, useContext, useEffect } from "react"

export type Theme = "dark" | "light" | "system"

export type ThemeProviderProps = {
  children: React.ReactNode
  defaultTheme?: Theme
  storageKey?: string
}

export type ThemeProviderState = {
  theme: Theme
  setTheme: (theme: Theme) => void
}

const initialState: ThemeProviderState = {
  theme: "system",
  setTheme: () => null,
}

const ThemeProviderContext = createContext<ThemeProviderState>(initialState)

/**
 * One theme for now: light. Alex, 22/10: dark mode is switched off during the design pass, so every
 * screen is designed and checked in one view; whether it comes back (or becomes the only option) is
 * decided when the pass is done. The `.dark` tokens stay in styles/index.css untouched, and this
 * provider keeps its API so turning it back on is a change here and two menu items.
 *
 * A stored "dark" from before is cleared, or that browser would keep the class forever.
 */
export function ThemeProvider({
  children,
  storageKey = "chatnutri-ui-theme",
  defaultTheme: _unused,
  ...props
}: ThemeProviderProps) {
  useEffect(() => {
    const root = window.document.documentElement
    root.classList.remove("dark")
    root.classList.add("light")
    try { localStorage.removeItem(storageKey) } catch { /* private mode: nothing stored anyway */ }
  }, [storageKey])

  const value: ThemeProviderState = { theme: "light", setTheme: () => null }

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext)

  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider")

  return context
}
